import { useEffect, useState, type ReactNode } from 'react';
import { HomeownersLayout } from './homeowners-layout';
import { NoClaimsLayout } from './no-claims-layout';
import { ResidentsLayout } from './residents-layout';

type UserClaim = 'homeowner' | 'resident';

// Returns `undefined` when the user holds neither claim — the "no claims" case
// the component below renders `NoClaimsLayout` for.
function getInitialClaim(
  isHomeOwner?: boolean,
  isResident?: boolean,
): UserClaim | undefined {
  if (isHomeOwner) return 'homeowner';
  if (isResident) return 'resident';
}

const HOMEOWNERS_SECTIONS = ['portfolio', 'homeowners'];
const RESIDENTS_SECTIONS = ['home', 'tours-and-apps', 'favorites', 'residents'];

type AccountsLayoutProps = {
  userClaims?: { isHomeOwner?: boolean; isResident?: boolean };
  section?: string;
  // Consumed by the claim-specific layouts this forwards `props` to, so they
  // have to be named here — an index signature alone leaves the spread with
  // "no properties in common" with those components' prop types.
  comeFromTheMobileApp?: boolean;
  children?: ReactNode;
  // Anything else is forwarded untouched.
  [key: string]: any;
};

export function AccountsLayout(props: AccountsLayoutProps) {
  const { userClaims, section } = props;
  const { isHomeOwner, isResident } = userClaims || {};
  const [currentClaim, setCurrentClaim] = useState(
    getInitialClaim(isHomeOwner, isResident),
  );

  // This allows us to keep the previous claim when changing to tabs not tied to any claim, like profile.
  useEffect(() => {
    // Guarded because `section` is optional; `includes(undefined)` was already
    // false, so this is the same outcome with a narrower type.
    if (section && HOMEOWNERS_SECTIONS.includes(section)) {
      setCurrentClaim('homeowner');
    } else if (section && RESIDENTS_SECTIONS.includes(section)) {
      setCurrentClaim('resident');
    }
  }, [section, setCurrentClaim]);

  if (currentClaim === 'homeowner') {
    return <HomeownersLayout {...props} isResident={Boolean(isResident)} />;
  } else if (currentClaim === 'resident') {
    return <ResidentsLayout {...props} isHomeOwner={isHomeOwner} />;
  }

  return <NoClaimsLayout {...props} />;
}
